import { keyframes } from '@emotion/react'
import styled from '@emotion/styled'
import { textAlign, TextAlignProps } from 'styled-system'

import { Colors, colors } from '../constants/colors'

import { Box } from './Flex'

export type AvailableColors = keyof typeof colors
type ColorProp = { color?: AvailableColors }

export const Label = styled.span<ColorProp & TextAlignProps>`
  font-size: 13px;
  font-weight: 800;
  line-height: 140%;
  letter-spacing: -0.078px;
  ${textAlign};

  ${({ color }) => color && `color: ${colors[color]};`}
`

export const TooltipText = styled(Label)`
  max-width: 300px;
  text-wrap: pretty;

  p + p {
    margin-top: 0.5rem;
  }
`

export const InputLabel = styled.span<ColorProp & TextAlignProps>`
  font-size: 12.5px;
  font-weight: 500;
  font-weight: 600;
  line-height: 140%;
  letter-spacing: -0.078px;
  ${textAlign};

  ${({ color }) => color && `color: ${colors[color]};`}
`

export const ControlLabel = styled.span<ColorProp & TextAlignProps>`
  font-family: Hauora;
  font-size: 13px;
  font-style: normal;
  font-weight: 800;
  line-height: 140%;
  letter-spacing: 0.39px;
  text-transform: uppercase;
  ${textAlign};

  ${({ color }) => color && `color: ${colors[color]};`}
`

export const ControlErrorMessage = styled.span`
  color: ${colors.red};
  font-family: Hauora;
  font-size: 13px;
  font-style: normal;
  font-weight: 600;
  line-height: 160%;
  letter-spacing: -0.078px;
  text-wrap: pretty;

  &::first-letter {
    text-transform: capitalize;
  }
`

export const SmallLabel = styled(Label)<ColorProp>`
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.3px;

  ${({ color }) => color && `color: ${colors[color]};`}
`

export const InfoLabel = styled(Label)<ColorProp>`
  opacity: 0.5;
  ${({ color = 'grey' }) => color && `color: ${colors[color]};`}
`

export const Headline = styled.h1`
  font-size: 20px;
  font-style: normal;
  font-weight: 800;
  line-height: 140%;
  letter-spacing: -0.3px;
  margin: 0;
`

export const Headline2 = styled.h2`
  font-size: 17px;
  font-style: normal;
  font-weight: 800;
  line-height: 140%;
  letter-spacing: -0.255px;
  margin: 0;
`

export const Item = styled.span<ColorProp>`
  font-size: 15px;
  font-style: normal;
  font-weight: 800;
  line-height: 140%;
  letter-spacing: -0.09px;

  ${({ color }) => color && `color: ${colors[color]};`}
`

export const Tag = styled(Label)<{ disabled?: boolean; color?: Colors }>`
  display: block;
  height: 24px;
  padding: 2px 8px;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
  background-color: ${colors.darkBlue4};
  border-radius: 9999px;
  font-style: normal;
  font-weight: 800;

  max-width: 7.75rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  ${({ disabled }) =>
    disabled && `background-color: ${colors.darkBlue3}; opacity: 0.5;`}
`

export const SuccessTag = styled(Tag)<{ disabled?: boolean }>`
  background-color: ${colors.green};
  color: ${colors.darkBlue1};
`

export const Hr = styled.hr`
  border-top: 1px solid ${colors.grey};
  opacity: 0.1;
  width: 100%;
  margin: 0;
`

const pulse = keyframes`
  0% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
  100% {
    opacity: 1;
  }
`

export const SkeletonText = styled(Box)`
  display: flex;
  padding: 2px 8px;
  justify-content: center;
  align-items: center;
  border-radius: 999px;
  background-color: ${colors.darkBlue4};
  animation: ${pulse} 1.5s infinite ease-in-out;
`
